import * as Sentry from '@sentry/react';
import * as Y from 'yjs';
import { Awareness, applyAwarenessUpdate, encodeAwarenessUpdate } from 'y-protocols/awareness';
import type { PersistenceAdapter } from './persistence-adapter';
import { fireSyncComplete } from './sync-event-bus';

export type YjsRoomKind = 'document' | 'chat' | 'canvas' | 'space';

export interface YjsRegistryEntry {
  doc: Y.Doc;
  awareness: Awareness;
  kind: YjsRoomKind;
  refCount: number;
  viewerCount: number;
  localReady: boolean;
  serverSynced: boolean;
  graceTimer: ReturnType<typeof setTimeout> | null;
  deleted: boolean; // true after markDeleted phase 1; cleared on resurrection
}

const GRACE_PERIOD_MS = 5000;
const KEEPALIVE_MS = 15_000;

export interface RegistryCallbacks {
  onLocalReady: (id: string) => void;
  onLocalUpdate: (id: string, update: Uint8Array) => void;
  onLocalAwareness: (id: string, update: Uint8Array) => void;
  onTornDown: (id: string) => void;
  onDocDeleted?: (id: string, actor: 'local' | 'remote', kind: YjsRoomKind) => void;
}

export class YjsRegistry {
  private entries = new Map<string, YjsRegistryEntry>();
  private updateHandlers = new Map<string, (update: Uint8Array, origin: unknown) => void>();
  private awarenessHandlers = new Map<string, (changes: { added: number[]; updated: number[]; removed: number[] }, origin: string | null) => void>();
  private keepaliveTimers = new Map<string, ReturnType<typeof setInterval>>();

  constructor(
    private readonly persistence: PersistenceAdapter,
    private readonly callbacks: RegistryCallbacks,
  ) {}

  async subscribe(id: string, kind: YjsRoomKind): Promise<Y.Doc> {
    const existing = this.entries.get(id);
    if (existing) {
      existing.refCount++;
      if (existing.graceTimer !== null) {
        clearTimeout(existing.graceTimer);
        existing.graceTimer = null;
      }
      // Resurrection: cancel any pending markDeleted teardown
      existing.deleted = false;
      return existing.doc;
    }

    const doc = new Y.Doc();
    const awareness = new Awareness(doc);
    const entry: YjsRegistryEntry = { doc, awareness, kind, refCount: 1, viewerCount: 0, localReady: false, serverSynced: false, graceTimer: null, deleted: false };
    this.entries.set(id, entry);

    const updateHandler = (update: Uint8Array, origin: unknown) => {
      if (origin === 'server') return;
      this.callbacks.onLocalUpdate(id, update);
    };
    const awarenessHandler = (
      changes: { added: number[]; updated: number[]; removed: number[] },
      origin: string | null,
    ) => {
      if (origin === 'server') return;
      const changedClients = [...changes.added, ...changes.updated, ...changes.removed];
      const update = encodeAwarenessUpdate(awareness, changedClients);
      this.callbacks.onLocalAwareness(id, update);
    };
    doc.on('updateV2', updateHandler);
    awareness.on('update', awarenessHandler);
    this.updateHandlers.set(id, updateHandler);
    this.awarenessHandlers.set(id, awarenessHandler);
    try {
      await this.persistence.bindDocAndSync(id, doc);
    } catch (err) {
      Sentry.captureException(err);
      console.error(`[YjsRegistry] persistence bind failed for ${id}:`, err);
    }

    entry.localReady = true;
    this.callbacks.onLocalReady(id);
    return doc;
  }

  unsubscribe(id: string): void {
    const entry = this.entries.get(id);
    if (!entry) return;
    if (entry.refCount <= 0) {
      console.warn(`[YjsRegistry] unsubscribe called with refCount=${entry.refCount} for ${id}`);
      return;
    }
    entry.refCount--;
    if (entry.refCount > 0) return;
    // Broadcast null state so peers immediately remove this client's cursor.
    // This fires the awarenessHandler which encodes and sends via onLocalAwareness.
    entry.awareness.setLocalState(null);
    // Start grace timer — teardown after GRACE_PERIOD_MS if not re-subscribed
    entry.graceTimer = setTimeout(() => this.teardown(id), GRACE_PERIOD_MS);
  }

  async deletePermanently(id: string): Promise<void> {
    await this.persistence.deleteYjsState(id);
    this.teardown(id);
  }

  /**
   * Overwrite the local IndexedDB state for a doc with the given snapshot doc,
   * then tear down the registry entry. On next mount the editor re-subscribes,
   * loads the snapshot state from IndexedDB immediately (works offline), and
   * once online syncs it back to the server automatically.
   */
  async resetLocalState(id: string, snapshotDoc: Y.Doc): Promise<void> {
    await this.persistence.overwriteYjsState(id, snapshotDoc);
    this.teardown(id);
  }

  /**
   * Drop everything this client holds for a doc — its Y.Doc and its stored
   * state — and subscribe again, so the server's state replaces it. Used when
   * the server refuses a local change (the user may only read the doc): the
   * Y.Doc cannot un-apply it, and left in place it would be resent on every
   * reconnect. Subscribers re-bind to the new doc on the next onLocalReady.
   */
  async discardLocalChanges(id: string): Promise<void> {
    const entry = this.entries.get(id);
    if (!entry) return;
    const { kind, refCount, viewerCount } = entry;
    if (entry.graceTimer !== null) clearTimeout(entry.graceTimer);
    this.detachHandlers(id, entry);
    this.entries.delete(id);
    entry.awareness.destroy();
    await this.persistence.unbindDoc(id);
    await this.persistence.deleteYjsState(id);
    entry.doc.destroy();
    await this.subscribe(id, kind);
    const fresh = this.entries.get(id);
    if (!fresh) return;
    fresh.refCount = refCount;
    for (let i = 0; i < viewerCount; i++) this.startViewing(id);
  }

  getDoc(id: string): Y.Doc | undefined {
    return this.entries.get(id)?.doc;
  }

  getAwareness(id: string): Awareness | undefined {
    return this.entries.get(id)?.awareness;
  }

  getEntry(id: string): YjsRegistryEntry | undefined {
    return this.entries.get(id);
  }

  getAllIds(): string[] {
    return Array.from(this.entries.keys());
  }

  rebroadcastAllAwareness(): void {
    for (const [id, entry] of this.entries) {
      const state = entry.awareness.getLocalState();
      if (!state) continue;
      const update = encodeAwarenessUpdate(entry.awareness, [entry.awareness.clientID]);
      this.callbacks.onLocalAwareness(id, update);
    }
  }

  startViewing(id: string): void {
    const entry = this.entries.get(id);
    if (!entry) return;
    entry.viewerCount++;
    if (entry.viewerCount === 1) {
      // First viewer — start keepalive
      this.keepaliveTimers.set(id, setInterval(() => {
        const state = entry.awareness.getLocalState();
        if (!state) return;
        const update = encodeAwarenessUpdate(entry.awareness, [entry.awareness.clientID]);
        this.callbacks.onLocalAwareness(id, update);
      }, KEEPALIVE_MS));
    }
  }

  stopViewing(id: string): void {
    const entry = this.entries.get(id);
    if (!entry) return;
    entry.viewerCount = Math.max(0, entry.viewerCount - 1);
    if (entry.viewerCount === 0) {
      // Last viewer gone — stop keepalive and null awareness
      const timer = this.keepaliveTimers.get(id);
      if (timer) clearInterval(timer);
      this.keepaliveTimers.delete(id);
      entry.awareness.setLocalState(null);
    }
  }

  applyServerUpdate(id: string, update: Uint8Array): void {
    const entry = this.entries.get(id);
    if (!entry) return;
    Y.applyUpdateV2(entry.doc, update, 'server');
  }

  applyServerAwareness(id: string, update: Uint8Array): void {
    const entry = this.entries.get(id);
    if (!entry) return;
    applyAwarenessUpdate(entry.awareness, update, 'server');
  }

  markServerSynced(id: string): void {
    const entry = this.entries.get(id);
    if (entry) {
      entry.serverSynced = true;
      fireSyncComplete(id);
    }
  }

  encodeStateVector(id: string): Uint8Array | null {
    const entry = this.entries.get(id);
    if (!entry) return null;
    return Y.encodeStateVector(entry.doc);
  }

  encodeStateAsUpdate(id: string, stateVector?: Uint8Array): Uint8Array | null {
    const entry = this.entries.get(id);
    if (!entry) return null;
    return Y.encodeStateAsUpdateV2(entry.doc, stateVector);
  }

  markDeleted(id: string, actor: 'local' | 'remote'): void {
    const entry = this.entries.get(id);
    if (!entry) return;
    if (entry.deleted) return; // already marked
    if (entry.graceTimer) { clearTimeout(entry.graceTimer); entry.graceTimer = null; }
    entry.deleted = true;
    // Phase 1: signal consumers synchronously — editors should switch to read-only or unmount
    this.callbacks.onDocDeleted?.(id, actor, entry.kind);
    // Phase 2: destroy after React has had a commit cycle to unmount
    queueMicrotask(() => {
      requestAnimationFrame(() => {
        if (!this.entries.get(id)?.deleted) return; // resurrected
        this.teardown(id);
      });
    });
  }

  disposeAll(): void {
    for (const id of Array.from(this.entries.keys())) {
      this.teardown(id);
    }
  }

  private detachHandlers(id: string, entry: YjsRegistryEntry): void {
    const updateHandler = this.updateHandlers.get(id);
    const awarenessHandler = this.awarenessHandlers.get(id);
    if (updateHandler) entry.doc.off('updateV2', updateHandler);
    if (awarenessHandler) entry.awareness.off('update', awarenessHandler);
    this.updateHandlers.delete(id);
    this.awarenessHandlers.delete(id);
    const keepalive = this.keepaliveTimers.get(id);
    if (keepalive) clearInterval(keepalive);
    this.keepaliveTimers.delete(id);
  }

  private teardown(id: string): void {
    const entry = this.entries.get(id);
    if (!entry) return;
    if (entry.graceTimer !== null) {
      clearTimeout(entry.graceTimer);
      entry.graceTimer = null;
    }
    this.detachHandlers(id, entry);
    // The entry is removed synchronously so a re-subscribe cannot resurrect a
    // doc that is already being torn down.
    this.entries.delete(id);
    entry.awareness.destroy();

    // Destroying the doc emits `destroy`, which y-indexeddb answers by closing
    // its IndexedDB connection. Its update handler guards on `this.db` being
    // truthy but not on `_destroyed`, so an update landing between close() and
    // the connection draining calls transact() on a closing database and
    // rejects (LUMEN-4T). Waiting for unbindDoc to finish closing means no
    // listener is left to fire an update into a dying connection.
    // Promise.resolve wraps the call so an adapter that returns undefined —
    // a test double, or an older implementation — still tears the doc down
    // rather than throwing here.
    void Promise.resolve(this.persistence.unbindDoc(id))
      .catch(() => {
        // A failed close must not strand the doc — tear it down regardless.
      })
      .then(() => {
        entry.doc.destroy();
        this.callbacks.onTornDown(id);
      });
  }
}
